'use client';

import { type Dispatch, type SetStateAction, createContext, useContext, useState } from 'react';
import type { AnyUserCtx } from '@lib/auth/decorators/types';
import { useAuthShellPending } from './auth-shell-pending-context';
const CurrentUserContext = createContext<AnyUserCtx | null>(null);
const UpgradeAuthSessionContext = createContext<Dispatch<SetStateAction<AnyUserCtx | null>> | null>(null);

/** Holds the permissive `resolveAuth()` session. */
export function AuthSessionProvider({
  authSession: initialAuthSession,
  children
}: {
  authSession: AnyUserCtx | null;
  children: React.ReactNode;
}) {
  const [authSession, setAuthSession] = useState(initialAuthSession);
  return <UpgradeAuthSessionContext.Provider value={setAuthSession} data-sentry-element="UpgradeAuthSessionContext.Provider" data-sentry-component="AuthSessionProvider" data-sentry-source-file="current-user-context.tsx">
      <CurrentUserContext.Provider value={authSession} data-sentry-element="CurrentUserContext.Provider" data-sentry-source-file="current-user-context.tsx">
        {children}
      </CurrentUserContext.Provider>
    </UpgradeAuthSessionContext.Provider>;
}

/** Replaces the shell hint with the deferred `resolveAuth()` session. */
export function useUpgradeAuthSessionFromStream(): Dispatch<SetStateAction<AnyUserCtx | null>> {
  const upgrade = useContext(UpgradeAuthSessionContext);
  if (!upgrade) {
    throw new Error('useUpgradeAuthSessionFromStream must run under AuthSessionProvider');
  }
  return upgrade;
}

/** Full auth session, or `null` when unauthenticated / outside a provider. */
export function useAuthSession(): AnyUserCtx | null {
  return useContext(CurrentUserContext);
}

/**
 * Clerk user id only.
 *
 * Returns `null` for anon, scoped, and unauthenticated sessions. Anon
 * sessions still carry a `userId` on the auth ctx; we hide it here to
 * preserve backwards compatibility with callers that treat a non-null id
 * as a signed-in Clerk user.
 */
export function useCurrentUserId(): string | null {
  const authShellPending = useAuthShellPending();
  const auth = useAuthSession();
  if (authShellPending) {
    return null;
  }
  return auth && !auth.isAnon ? auth.userId : null;
}